import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import AltRouteOutlinedIcon from '@mui/icons-material/AltRouteOutlined';
import { ConfirmDialog, DataTable, DataTableColumn, EmptyState, Notice, PageHeader } from '@routes-plan/ui';
import { StandardModalBody, StandardModalFooter } from '../../../components/modales/StandardModal';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import LocationPicker, { isLocationComplete } from '../shared/LocationPicker';
import { INVENTORY_ROUTES, OfficeOption, ShowAlerts, apiErrorMessage, fetchOffices, formatMoney, utilityLabel } from '../shared/inventoryApi';
import { ActionIcon, CompanyMoneyField, FieldGrid, FormFooter, IntField, PageBody, SaveButton, Section, useInventoryAlert } from '../shared/ui';
import { useT } from '../../../i18n';
import { setPageTitle } from '../../../utils/pageTitle';

interface AssignmentRow {
  id: number;
  office: { id: number; name: string } | null;
  route: { id: number; name: string; officeId: number | null } | null;
  active: boolean;
  price: number | null;
  marginPercent: number | null;
  minStock: number | null;
}

type Draft = { active: boolean; price: string; marginPercent: string; minStock: string };

/** Dónde está habilitado un producto: sedes (heredan las rutas) y excepciones por ruta. */
interface ProductAssignmentsProps extends ShowAlerts {
  /** Producto cuando se abre como modal (en página viene por la URL). */
  productId?: number;
  /** 'modal': dentro de StandardModal (composed). */
  variant?: 'page' | 'modal';
  onClose?: () => void;
  /** Se llama después de cada cambio guardado (p. ej. para recargar el listado). */
  onChanged?: () => void;
}

const ProductAssignments: React.FC<ProductAssignmentsProps> = ({
  showLoading,
  hideLoading,
  productId,
  variant = 'page',
  onClose,
  onChanged,
}) => {
  const t = useT();
  const showAlert = useInventoryAlert();
  const params = useParams();
  const navigate = useNavigate();
  const id = productId != null ? String(productId) : params.id;
  const inModal = variant === 'modal';
  const { canUpdate } = useResourcePermissions(INVENTORY_ROUTES.products);
  const [product, setProduct] = useState<{
    title: string;
    price: number;
    marginPercent: number | null;
    cost: number;
    minStock: number | null;
    officeId: number | null;
    officeName: string | null;
  } | null>(null);
  const [rows, setRows] = useState<AssignmentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [drafts, setDrafts] = useState<Record<number, Draft>>({});
  const [offices, setOffices] = useState<OfficeOption[]>([]);
  const [newLocation, setNewLocation] = useState<{ officeId: number | null; routeId: number | null }>({ officeId: null, routeId: null });
  const [newPrice, setNewPrice] = useState('');
  const [newMinStock, setNewMinStock] = useState('');
  const [locationError, setLocationError] = useState('');
  const [toRemove, setToRemove] = useState<AssignmentRow | null>(null);
  const [removing, setRemoving] = useState(false);
  if (!inModal) setPageTitle('products.assignRoute');

  const load = useCallback(async () => {
    showLoading();
    try {
      const [{ data: p }, { data: a }, officeList] = await Promise.all([
        axios.get(`products/${id}`),
        axios.get(`products/${id}/assignments`),
        fetchOffices(),
      ]);
      setProduct({
        title: p.title,
        price: Number(p.price || 0),
        marginPercent: p.marginPercent == null ? null : Number(p.marginPercent),
        cost: Number(p.costPrice || 0),
        minStock: p.minStock == null ? null : Number(p.minStock),
        officeId: p.officeId ?? null,
        officeName: p.office?.name ?? null,
      });
      if (p.officeId) setNewLocation({ officeId: Number(p.officeId), routeId: null });
      const list: AssignmentRow[] = Array.isArray(a?.data) ? a.data : [];
      setRows(list);
      setDrafts(
        Object.fromEntries(
          list.map((r) => [
            r.id,
            {
              active: Boolean(r.active),
              price: r.price == null ? '' : String(r.price),
              marginPercent: r.marginPercent == null ? '' : String(Number(r.marginPercent)),
              minStock: r.minStock == null ? '' : String(r.minStock),
            },
          ]),
        ),
      );
      setOffices(officeList);
    } catch (error) {
      showAlert(apiErrorMessage(error, t('products.assignments.loadError')), 'error');
    } finally {
      setLoading(false);
      hideLoading();
    }
  }, [id, showAlert, showLoading, hideLoading, t]);

  useEffect(() => {
    load();
  }, [load]);

  const upsert = async (assignment: Record<string, unknown>, okMessage: string) => {
    setSaving(true);
    showLoading();
    try {
      await axios.put(`products/${id}/assignments`, { assignments: [assignment] });
      showAlert(okMessage, 'success');
      await load();
      onChanged?.();
      return true;
    } catch (error) {
      showAlert(apiErrorMessage(error, t('products.assignments.saveError')), 'error');
      return false;
    } finally {
      setSaving(false);
      hideLoading();
    }
  };

  const saveRow = (row: AssignmentRow) => {
    const d = drafts[row.id];
    if (!d) return;
    upsert(
      {
        ...(row.route ? { routeId: row.route.id } : { officeId: row.office?.id }),
        active: d.active,
        price: d.price === '' ? null : Number(d.price),
        minStock: d.minStock === '' ? null : Number(d.minStock),
      },
      t('products.assignments.saved'),
    );
  };

  const removeRow = async () => {
    if (!toRemove) return;
    setRemoving(true);
    try {
      await axios.delete(`product-assignments/${toRemove.id}`);
      showAlert(t('products.assignments.removed'), 'success');
      setToRemove(null);
      await load();
      onChanged?.();
    } catch (error) {
      showAlert(apiErrorMessage(error, t('products.assignments.removeError')), 'error');
    } finally {
      setRemoving(false);
    }
  };

  const addAssignment = async () => {
    if (!isLocationComplete(newLocation) || (product?.officeId && !newLocation.routeId)) {
      setLocationError(product?.officeId ? t('products.assignments.selectRoute') : t('products.assignments.selectOfficeRoute'));
      return;
    }
    const ok = await upsert(
      {
        ...(newLocation.routeId ? { routeId: newLocation.routeId } : { officeId: newLocation.officeId }),
        active: true,
        price: newPrice === '' ? null : Number(newPrice),
        minStock: newMinStock === '' ? null : Number(newMinStock),
      },
      t('products.assignments.routeConfigured'),
    );
    if (ok) {
      setNewPrice('');
      setNewMinStock('');
    }
  };

  const setDraft = (rowId: number, patch: Partial<Draft>) =>
    setDrafts((prev) => ({ ...prev, [rowId]: { ...prev[rowId], ...patch } }));

  /** Valor que se usa si la fila no define uno propio: el de la sede de la ruta o, si no, el general. */
  const inherited = (row: AssignmentRow, field: 'price' | 'marginPercent' | 'minStock') => {
    if (row.route) {
      const officeRow = rows.find((r) => !r.route && r.office?.id === row.route?.officeId);
      const fromOffice = officeRow ? officeRow[field] : null;
      if (fromOffice !== null && fromOffice !== undefined) return { value: Number(fromOffice), from: t('products.assignments.fromOffice') };
    }
    const general = product ? product[field] : null;
    return { value: general === null || general === undefined ? null : Number(general), from: t('products.assignments.fromGeneral') };
  };
  const inheritedLabel = (row: AssignmentRow, field: 'price' | 'minStock') => {
    const { value, from } = inherited(row, field);
    if (value === null) return t('products.assignments.notDefined');
    const text = field === 'price' ? formatMoney(value) : String(value);
    return `${text} (${from})`;
  };
  /** Valor heredado, en gris dentro del campo vacío. */
  const inheritedShort = (row: AssignmentRow, field: 'price' | 'minStock') => {
    const { value } = inherited(row, field);
    if (value === null) return '—';
    return field === 'price' ? formatMoney(value) : String(value);
  };

  const columns: DataTableColumn<AssignmentRow>[] = [
    {
      id: 'place',
      header: t('products.assignments.place'),
      value: (r) =>
        r.route ? t('products.assignments.locationRoute', { name: r.route.name }) : t('products.assignments.locationOffice', { name: r.office?.name ?? '' }),
      sortable: true,
    },
    ...(product?.officeId
      ? []
      : [
          {
            id: 'scope',
            header: t('products.assignments.appliesTo'),
            value: (r: AssignmentRow) => (r.route ? t('products.assignments.scopeRoute') : t('products.assignments.scopeOffice')),
            hideBelow: 'sm' as const,
          },
        ]),
    {
      id: 'active',
      header: t('products.assignments.lends'),
      align: 'center',
      value: (r) => (drafts[r.id]?.active ? t('common.yes') : t('common.no')),
      render: (r) => (
        <Switch
          checked={Boolean(drafts[r.id]?.active)}
          disabled={!canUpdate}
          onChange={(e) => setDraft(r.id, { active: e.target.checked })}
          inputProps={{ 'aria-label': t('products.assignments.lends') }}
        />
      ),
    },
    {
      id: 'price',
      header: t('products.specialPrice'),
      value: (r) => (drafts[r.id]?.price ? Number(drafts[r.id].price) : null),
      render: (r) => (
        <Tooltip title={t('products.assignments.emptyEquals', { value: inheritedLabel(r, 'price') })}>
          <Box sx={{ width: 150 }}>
            <CompanyMoneyField
              size="small"
              fullWidth
              placeholder={inheritedShort(r, 'price')}
              value={drafts[r.id]?.price ?? ''}
              disabled={!canUpdate}
              onChange={(v) => setDraft(r.id, { price: v })}
              inputProps={{ 'aria-label': t('products.specialPrice') }}
            />
          </Box>
        </Tooltip>
      ),
    },
    {
      id: 'utility',
      header: t('products.utility'),
      align: 'right',
      // Informativa: precio que se usa en esta ruta − costo del producto.
      value: (r) => {
        const d = drafts[r.id];
        const priceHere = d?.price !== '' && d?.price !== undefined ? Number(d.price) : inherited(r, 'price').value ?? 0;
        return utilityLabel(product?.cost ?? 0, priceHere);
      },
    },
    {
      id: 'minStock',
      header: t('products.minStock'),
      value: (r) => (drafts[r.id]?.minStock ? Number(drafts[r.id].minStock) : null),
      render: (r) => (
        <Tooltip title={t('products.assignments.emptyEquals', { value: inheritedLabel(r, 'minStock') })}>
          <Box sx={{ width: 90 }}>
            <IntField
              size="small"
              fullWidth
              placeholder={inheritedShort(r, 'minStock')}
              value={drafts[r.id]?.minStock ?? ''}
              disabled={!canUpdate}
              onChange={(v) => setDraft(r.id, { minStock: v })}
              inputProps={{ 'aria-label': t('products.minStock') }}
            />
          </Box>
        </Tooltip>
      ),
    },
  ];

  const intro = product && (
    <Notice severity="info">
      {product.officeName ? (
        <>
          {t('products.assignments.introOfficePrefix')} <strong>{product.officeName}</strong>
          {t('products.assignments.introOfficeRest', { price: formatMoney(product.price) })}
          {product.cost > 0 ? t('products.assignments.introCost', { utility: utilityLabel(product.cost, product.price), cost: formatMoney(product.cost) }) : ''}
          {t('products.assignments.introOfficeEnd')}
        </>
      ) : (
        t('products.assignments.introNoOffice', { price: formatMoney(product.price) })
      )}
    </Notice>
  );

  const table = (
    <DataTable<AssignmentRow>
      rows={rows}
      columns={columns}
      getRowId={(r) => r.id}
      loading={loading}
      searchable={false}
      ariaLabel={t('products.assignments.tableTitle')}
      maxHeight={inModal ? 'none' : undefined}
      rowActions={(r) => (
        <>
          <ActionIcon
            label={t('common.save')}
            icon={<SaveOutlinedIcon fontSize="small" />}
            color="primary"
            allowed={canUpdate}
            deniedTooltip={t('common.noPermissionEdit')}
            onClick={() => saveRow(r)}
            disabled={saving}
          />
          <ActionIcon
            label={t('products.assignments.removePlace')}
            icon={<DeleteOutlineOutlinedIcon fontSize="small" />}
            color="error"
            allowed={canUpdate}
            deniedTooltip={t('common.noPermissionEdit')}
            onClick={() => setToRemove(r)}
          />
        </>
      )}
      emptyState={
        <EmptyState
          dense
          icon={<AltRouteOutlinedIcon fontSize="inherit" />}
          title={product?.officeName ? t('products.assignments.emptyTitle') : t('products.assignments.emptyNoConfig')}
          description={product?.officeName ? t('products.assignments.emptyOffice', { office: product.officeName }) : undefined}
        />
      }
    />
  );

  const saveRouteButton = (
    <SaveButton allowed={canUpdate} deniedTooltip={t('products.noPermissionAssignRoutes')} loading={saving} onClick={addAssignment}>
      {t('products.assignments.saveRoute')}
    </SaveButton>
  );

  const configFields = (
    <Section plain={inModal} title={t('products.assignments.configTitle')} description={t('products.assignments.configHelp')}>
      <Box sx={{ display: 'grid', gap: 2 }}>
        <LocationPicker
          offices={offices}
          value={newLocation}
          onChange={(v) => {
            setNewLocation(v);
            setLocationError('');
          }}
          lockedOfficeId={product?.officeId ?? null}
          allowOfficeOnly={!product?.officeId}
          size="medium"
        />
        {locationError && (
          <Typography variant="caption" color="error" role="alert" sx={{ mt: -1 }}>
            {locationError}
          </Typography>
        )}
        <FieldGrid>
          <CompanyMoneyField
            fullWidth
            label={t('products.assignments.newPriceLabel')}
            value={newPrice}
            onChange={setNewPrice}
            helperText={
              newPrice !== '' && product && product.cost > 0
                ? t('products.assignments.utilityOverCost', { utility: utilityLabel(product.cost, Number(newPrice)) })
                : product
                  ? t('products.assignments.emptyEquals', { value: formatMoney(product.price) })
                  : ' '
            }
          />
          <IntField fullWidth label={t('products.assignments.minStockOptional')} value={newMinStock} onChange={setNewMinStock} helperText=" " />
        </FieldGrid>
      </Box>
    </Section>
  );

  const confirm = (
    <ConfirmDialog
      open={Boolean(toRemove)}
      variant="danger"
      title={t('products.assignments.removeTitle')}
      message={t('products.assignments.confirmRemove')}
      confirmLabel={t('products.assignments.removePlace')}
      cancelLabel={t('common.cancel')}
      loading={removing}
      onConfirm={removeRow}
      onCancel={() => setToRemove(null)}
    />
  );

  if (inModal) {
    return (
      <>
        <StandardModalBody>
          <Box sx={{ display: 'grid', gap: 2 }}>
            {intro}
            {table}
            {canUpdate && (
              <>
                <Divider sx={{ my: 1 }} />
                {configFields}
              </>
            )}
          </Box>
        </StandardModalBody>
        <StandardModalFooter>
          <Button color="inherit" variant="outlined" onClick={onClose}>
            {t('common.close')}
          </Button>
          {canUpdate && saveRouteButton}
        </StandardModalFooter>
        {confirm}
      </>
    );
  }

  return (
    <PageBody>
      <PageHeader
        title={t('products.assignRoute')}
        description={product?.title}
        breadcrumbs={[{ label: t('products.list.title'), onClick: () => navigate(INVENTORY_ROUTES.products) }, { label: t('products.assignRoute') }]}
      />
      {intro}
      <Card>
        <CardContent>{table}</CardContent>
      </Card>
      {canUpdate && (
        <>
          {configFields}
          <FormFooter>
            <Button color="inherit" variant="outlined" onClick={() => navigate(INVENTORY_ROUTES.products)}>
              {t('common.back')}
            </Button>
            {saveRouteButton}
          </FormFooter>
        </>
      )}
      {confirm}
    </PageBody>
  );
};

export default ProductAssignments;
